import React from 'react';
import DashboardLineChart from '../DashboardLineChart/DashboardLineChart';
import { DashboardMetricsResponse } from '../../utils/api/Dashboard';
import { ReactComponent as ArmScoreTrendIcon } from '../../assets/icons/armScoreTrend.svg';
import { ReactComponent as FreshExamIcon } from '../../assets/icons/icPlusFreshExam.svg';
import { ReactComponent as PostExamIcon } from '../../assets/icons/icListPostExam.svg';
import { ReactComponent as ArmPrimerIcon } from '../../assets/icons/icLabArmPrimer.svg';
import { ReactComponent as CircleTrainingIcon } from '../../assets/icons/icCircleTraining.svg';
import {
  DashboardCardPlaceholder,
  ArmScoreCard,
  TotalArmStrengthCard,
  MetricCardHeader,
  MetricTitle,
  MetricInfoIcon,
  MetricInfoTooltipWrap,
  MetricTooltipBubble,
  MetricChartWrapper,
  MetricDelta,
  MetricSubText,
  MetricTrendIconWrapper,
  TestingList,
  TestingListItem,
  TestingLabel,
  TestingLabelIcon,
  TestingValue,
} from './sectionStyles';

/** Tooltip copy keyed by metric id for summary card help icons. */
const METRIC_HELP_COPY = {
  armScore:
    'Your ArmScore reflects the change in relative arm strength (arm strength divided by bodyweight) from your earliest to most recent exam within the selected date range.',
  totalArmStrength:
    'Your Arm Strength shows how much total strength your team has gained across all key movements (IR, ER, Scaption, and Grip) during the selected time period.',
  testing:
    'This shows the total number of ArmCare exams completed during the selected date range, including Fresh Exams, Post Exams, and Arm Primers.',
  training:
    'This reflects the total number of training sessions completed, including Strength, Hybrid, and Off Days.',
} as const;

/** Small “i” control with the same hover tooltip pattern as other dashboard metrics. */
function MetricHelpIcon({ label, text }: { label: string; text: string }) {
  return (
    <MetricInfoTooltipWrap>
      <MetricInfoIcon type="button" aria-label={label}>
        i
      </MetricInfoIcon>
      <MetricTooltipBubble role="tooltip">{text}</MetricTooltipBubble>
    </MetricInfoTooltipWrap>
  );
}

type SummaryCardsProps = {
  metrics?: DashboardMetricsResponse;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Four dashboard summary tiles: ArmScore and Total Arm Strength line charts, Testing and Training breakdown lists.
 * Prefers API `change`/`average` when present; falls back to computing delta/average from trend points.
 */
export const SummaryCards: React.FC<SummaryCardsProps> = ({ metrics, isLoading, isError }) => {
  if (isError) {
    return (
      <>
        <div>Unable to load dashboard metrics.</div>
      </>
    );
  }

  if (isLoading || !metrics) {
    return (
      <>
        <div>Loading metrics...</div>
      </>
    );
  }

  const armScoreTrend = metrics.armScore?.trend ?? [];
  const totalArmStrengthTrend = metrics.totalArmStrength?.trend ?? [];

  /** Change from first to last point in a trend (fallback when API omits `change`). */
  const calcDelta = (points: { value: number | null }[]) => {
    if (!points.length) return 0;
    const first = points[0].value ?? 0;
    const last = points[points.length - 1].value ?? 0;
    return last - first;
  };

  /** Mean of non-null point values, rounded (fallback when API omits `average`). */
  const calcAverage = (points: { value: number | null }[]) => {
    const vals = points.map(p => p.value).filter(v => v != null) as number[];
    if (!vals.length) return 0;
    return Math.round(vals.reduce((sum, v) => sum + v, 0) / vals.length);
  };

  const noArmScoreTrend = armScoreTrend.length === 0;
  const noStrengthTrend = totalArmStrengthTrend.length === 0;

  const armScoreDelta = metrics.armScore?.change ?? calcDelta(armScoreTrend);
  const armScoreAverage = metrics.armScore?.average ?? calcAverage(armScoreTrend);
  const strengthDelta = metrics.totalArmStrength?.change ?? calcDelta(totalArmStrengthTrend);
  const strengthAverage = metrics.totalArmStrength?.average ?? calcAverage(totalArmStrengthTrend);

  const testingTotal = metrics.testing?.totalExams ?? 0;
  const testingFresh = metrics.testing?.breakdown?.fresh ?? 0;
  const testingPost = metrics.testing?.breakdown?.post ?? 0;
  const testingArmPrimer = metrics.testing?.breakdown?.armPrimer ?? 0;

  const trainingTotal = metrics.training?.totalSessions ?? 0;
  const trainingStrength = metrics.training?.breakdown?.strength ?? 0;
  const trainingHybrid = metrics.training?.breakdown?.hybrid ?? 0;
  const trainingOffDay = metrics.training?.breakdown?.offDay ?? 0;

  /** Formatted delta with unicode minus for negative values (UI consistency). */
  const formatDelta = (value: number) => {
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return `${sign}${Math.abs(Math.round(value))}`;
  };

  return (
    <>
      <ArmScoreCard>
        <MetricCardHeader>
          <MetricTitle>ARMSCORE</MetricTitle>
          <MetricHelpIcon label="About ArmScore" text={METRIC_HELP_COPY.armScore} />
        </MetricCardHeader>
        <p>
          <strong>
            <MetricTrendIconWrapper positive={armScoreDelta > 0} negative={armScoreDelta < 0}>
              <ArmScoreTrendIcon />
            </MetricTrendIconWrapper>
            <MetricDelta positive={armScoreDelta > 0} negative={armScoreDelta < 0}>
              {formatDelta(armScoreDelta)}
            </MetricDelta>
          </strong>{' '}
          pts
        </p>
        <MetricSubText>Team Average: {armScoreAverage}</MetricSubText>
        <MetricChartWrapper>
          <DashboardLineChart
            data={armScoreTrend}
            valueLabel="ArmScore AVG"
            color={noArmScoreTrend ? '#9ca3af' : '#2f80ed'}
            gradientStops={
              noArmScoreTrend
                ? [
                    { offset: '0%', opacity: 0.25 },
                    { offset: '65%', opacity: 0.1 },
                    { offset: '100%', opacity: 0 },
                  ]
                : [
                    { offset: '0%', opacity: 0.5 },
                    { offset: '65%', opacity: 0.22 },
                    { offset: '100%', opacity: 0 },
                  ]
            }
          />
        </MetricChartWrapper>
      </ArmScoreCard>
      <TotalArmStrengthCard>
        <MetricCardHeader>
          <MetricTitle>TOTAL ARM STRENGTH</MetricTitle>
          <MetricHelpIcon label="About total arm strength" text={METRIC_HELP_COPY.totalArmStrength} />
        </MetricCardHeader>
        <p>
          <strong>
          <MetricTrendIconWrapper positive={strengthDelta > 0} negative={strengthDelta < 0}>
              <ArmScoreTrendIcon />
            </MetricTrendIconWrapper>
            <MetricDelta positive={strengthDelta > 0} negative={strengthDelta < 0}>
              {formatDelta(strengthDelta)}
            </MetricDelta>
          </strong>{' '}
          lbs
        </p>
        <MetricSubText>Team Average: {strengthAverage} lbs</MetricSubText>
        <MetricChartWrapper>
          <DashboardLineChart
            data={totalArmStrengthTrend}
            valueLabel="Total Arm Strength AVG"
            color={noStrengthTrend ? '#9ca3af' : '#27ae60'}
            gradientStops={
              noStrengthTrend
                ? [
                    { offset: '0%', opacity: 0.25 },
                    { offset: '65%', opacity: 0.1 },
                    { offset: '100%', opacity: 0 },
                  ]
                : [
                    { offset: '0%', opacity: 0.5 },
                    { offset: '65%', opacity: 0.22 },
                    { offset: '100%', opacity: 0 },
                  ]
            }
          />
        </MetricChartWrapper>
      </TotalArmStrengthCard>
      <DashboardCardPlaceholder>
        <MetricCardHeader>
          <MetricTitle>TESTING (TOTAL)</MetricTitle>
          <MetricHelpIcon label="About testing totals" text={METRIC_HELP_COPY.testing} />
        </MetricCardHeader>
        <p>
          <strong>{testingTotal.toLocaleString()}</strong> exams
        </p>
        <TestingList>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#DBEAFE" iconColor="#2563EB">
                <FreshExamIcon />
              </TestingLabelIcon>
              <span>Fresh Exams</span>
            </TestingLabel>
            <TestingValue>{testingFresh.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#E0E7FF" iconColor="#4F46E5">
                <PostExamIcon />
              </TestingLabelIcon>
              <span>Post Exams</span>
            </TestingLabel>
            <TestingValue>{testingPost.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#EBEDF2" iconColor="#475569">
                <ArmPrimerIcon />
              </TestingLabelIcon>
              <span>Arm Primer</span>
            </TestingLabel>
            <TestingValue>{testingArmPrimer.toLocaleString()}</TestingValue>
          </TestingListItem>
        </TestingList>
      </DashboardCardPlaceholder>
      <DashboardCardPlaceholder>
        <MetricCardHeader>
          <MetricTitle>TRAINING (TOTAL)</MetricTitle>
          <MetricHelpIcon label="About training totals" text={METRIC_HELP_COPY.training} />
        </MetricCardHeader>
        <p>
          <strong>{trainingTotal.toLocaleString()}</strong> sessions
        </p>
        <TestingList>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#9333EA" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Strength</span>
            </TestingLabel>
            <TestingValue>{trainingStrength.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#F97316" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Hybrid</span>
            </TestingLabel>
            <TestingValue>{trainingHybrid.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#38424D" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Off Day</span>
            </TestingLabel>
            <TestingValue>{trainingOffDay.toLocaleString()}</TestingValue>
          </TestingListItem>
        </TestingList>
      </DashboardCardPlaceholder>
    </>
  );
};

